import React, { useCallback, useMemo, useState } from "react";
import { StandardPageWrapper } from "../../../../components/layout/StandardPageWrapper";
import { Routes } from "../../../../root/routes";
import { AlertCard, Button, useSnackBar } from "@truevault/aquilo";
import { DataFlowsStickyHeader } from "../DataFlowsPageHeader";
import { useDataFlowsSourcesCompletion } from "../dataFlowsCompletion";
// Render-time-only cycle with the dashboard module, same shape as the Sources page's import.
import {
  CONSUMER_DATA_FLOWS_DASHBOARD_CONFIG,
  useDataFlowCollectionGroupsCompletion,
} from "../DataFlowsDashboardPage";
import { useOrganizationVendors } from "../../../../hooks";
import { purposeVendorEvidence } from "../recommendations/purposeRecommendations";
import {
  PURPOSES_RECOMMENDATIONS_DISMISSED_SLUG,
  useRecommendationDismissal,
} from "../recommendations/useRecommendationDismissal";
import { useDataFlowsOnboardingFooterGate } from "../dataFlowsCompletion";
import { DEEP_LINK_FOCUS_STYLE, useDataFlowsDeepLinkFocus } from "../dataFlowsDeepLink";
import { useInvalidateOnboardingProgress } from "../../../../../common/hooks/useOnboardingProgress";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../../../hooks";
import { ProcessingRegion } from "../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../../../common/service/server/dto/OrganizationDto";
import {
  UseDefaultActivityCheckboxes,
  useDefaultActivityCheckboxes,
  useProcessingActivites,
} from "../../../../hooks/useProcessingActivities";
import { useCollectionGroups } from "../../../../hooks/useCollectionGroups";
import { PageLoading } from "../../../../components";
import { CollectionPurposeCollapseCard } from "../../../get-compliant/consumer-collection/CollectionPurposes";
import { useAllCollectedPIC } from "../../../../../common/hooks/dataMapHooks";
import { useDataMap } from "../../../../../common/hooks/api/useDataMap";
import { useHistory } from "react-router";
import { ProgressFooter, ProgressFooterActions } from "../../../get-compliant/ProgressFooter";
import { PurposesExplanatoryCard } from "../DataFlowsExplanatoryCards";
import { CustomPurposeNameModal, RemoveCustomPurposeModal } from "./PurposesModals";
import { Api } from "../../../../../common/service/Api";
import { ProcessingActivityDto } from "../../../../../common/service/server/dto/ProcessingActivityDto";

/**
 * Selected defaults and US-enabled custom purposes each need a saved category.
 * Optimistic association changes cannot complete the page until they finish saving.
 */
export function isPurposesPageComplete(
  defCheckboxes: UseDefaultActivityCheckboxes,
  activities?: ProcessingActivityDto[],
): boolean {
  const selectedDefaultActivities = defCheckboxes.props.checked
    .filter((id) => defCheckboxes.props.defaults.some((activity) => activity.id === id))
    .map((id) =>
      activities?.find(
        (activity) => activity.defaultId === id && activity.regions.includes("UNITED_STATES"),
      ),
    );
  const selectedCustomActivities = (activities ?? []).filter(
    (activity) =>
      isCustomProcessingActivity(activity) && activity.regions.includes("UNITED_STATES"),
  );
  const selectedActivities = [...selectedDefaultActivities, ...selectedCustomActivities];
  return (
    selectedActivities.length > 0 &&
    selectedActivities.every(
      (activity) =>
        activity != null &&
        activity.processedPic.length > 0 &&
        (activity._local?.processedPicPendingUpdates?.length ?? 0) === 0,
    )
  );
}

/** Matches Get Compliant: US for all orgs; EEA/UK and Canada only when those features are enabled. */
export function processingActivityCreateRegions(
  org: Pick<OrganizationDto, "featureGdpr" | "featurePipeda">,
): ProcessingRegion[] {
  const regions: ProcessingRegion[] = ["UNITED_STATES"];
  if (org.featureGdpr) {
    regions.push("EEA_UK");
  }
  if (org.featurePipeda) {
    regions.push("CANADA");
  }
  return regions;
}

export function isCustomProcessingActivity(activity: ProcessingActivityDto): boolean {
  return !activity.defaultId && !activity.autocreationSlug;
}

type PurposeNameModalState = { mode: "add" } | { mode: "edit"; activity: ProcessingActivityDto };

type RemovePurposeModalState = {
  activity: ProcessingActivityDto;
};

export function DataFlowsPurposesPage() {
  const history = useHistory();

  const orgId = usePrimaryOrganizationId();
  const [org] = usePrimaryOrganization();
  const [consumerGroups] = useCollectionGroups(
    orgId,
    undefined,
    /* excludeTypeSlug = */ ["EMPLOYMENT"],
  );
  const [activities, , refreshActivities, assocationMutation] = useProcessingActivites(orgId);
  const [orgVendors] = useOrganizationVendors(
    orgId,
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    /* context = */ ["CONSUMER"],
    /* onlyUseSettled = */ true,
  );
  const recommendationDismissal = useRecommendationDismissal(
    orgId,
    PURPOSES_RECOMMENDATIONS_DISMISSED_SLUG,
  );
  // Tab state mirrors the dashboard: Purposes sits between the groups and Sources, and Sources
  // stays locked until the groups and these purposes are done (unless the org finished onboarding).
  const stepsCompletion = useDataFlowCollectionGroupsCompletion({
    config: CONSUMER_DATA_FLOWS_DASHBOARD_CONFIG,
    groups: consumerGroups && consumerGroups.length > 0 ? consumerGroups : undefined,
  });
  const sourcesCompletion = useDataFlowsSourcesCompletion(orgId, consumerGroups ?? []);

  const createRegions = useMemo(
    () => (org ? processingActivityCreateRegions(org) : (["UNITED_STATES"] as ProcessingRegion[])),
    [org],
  );

  const defCheckboxes = useDefaultActivityCheckboxes(
    orgId,
    activities,
    /* region = */ "UNITED_STATES",
    createRegions,
    refreshActivities,
  );

  const { data: dataMap, request: dataMapRequest } = useDataMap(orgId);
  const collectedPic = useAllCollectedPIC(dataMap, ["EMPLOYMENT"]);

  const notProcessedPic = useMemo(() => {
    const processedPicIds = new Set(
      (activities ?? []).flatMap((activity) => activity.processedPic),
    );
    return collectedPic.filter((pic) => !processedPicIds.has(pic.id));
  }, [activities, collectedPic]);

  const customActivities = useMemo(
    () => activities?.filter(isCustomProcessingActivity) ?? [],
    [activities],
  );

  const customCheckedIds = useMemo(
    () => customActivities.filter((a) => a.regions.includes("UNITED_STATES")).map((a) => a.id),
    [customActivities],
  );

  const existingPurposeNames = useMemo(() => activities?.map((a) => a.name) ?? [], [activities]);

  // DES-30 recommendations: only purposes with vendor evidence from the org's own inventory
  // ("Because you are using: …") are recommended — popularity alone is not enough (crit c_6a504d).
  const vendorEvidenceByPurpose = useMemo(
    () => purposeVendorEvidence(orgVendors ?? []),
    [orgVendors],
  );
  const recommendedDefaults = useMemo(
    () =>
      (defCheckboxes?.props.defaults ?? []).filter((d) => vendorEvidenceByPurpose[d.name]?.length),
    [defCheckboxes?.props.defaults, vendorEvidenceByPurpose],
  );
  const recommendationForDefault = (name: string) => {
    const vendors = vendorEvidenceByPurpose[name];
    if (!vendors?.length) {
      return undefined;
    }
    return {
      vendors,
    };
  };

  const showSnackbar = useSnackBar();
  // Arriving from the finished data-map diagram: scroll to the purpose that was clicked.
  const focusedPurposeId = useDataFlowsDeepLinkFocus(
    "purpose",
    "purpose",
    Boolean(defCheckboxes?.props?.defaults?.length),
  );
  const [isSelectingRecommended, setIsSelectingRecommended] = useState(false);
  const [nameModalState, setNameModalState] = useState<PurposeNameModalState | null>(null);
  const [removeModalState, setRemoveModalState] = useState<RemovePurposeModalState | null>(null);

  // Purposes are org-wide; the footer gate keeps latched organizations out of onboarding.
  const showOnboardingFooter = useDataFlowsOnboardingFooterGate(
    /* isHr = */ false,
    !defCheckboxes || !activities || isPurposesPageComplete(defCheckboxes, activities),
  );
  const invalidateOnboardingProgress = useInvalidateOnboardingProgress(orgId);

  const handleCustomToggle = useCallback(
    async (activityId: string, isChecked: boolean) => {
      const existingActivity = activities?.find((a) => a.id === activityId);
      if (!existingActivity) {
        return;
      }

      if (isChecked) {
        const regions = [...new Set([...existingActivity.regions, ...createRegions])];
        await Api.processingActivities.patchUpdateProcessingActivity(orgId, existingActivity.id, {
          regions,
          name: undefined,
          lawfulBases: undefined,
        });
      } else {
        await Api.processingActivities.deleteProcessingActivityRegion(
          orgId,
          existingActivity.id,
          createRegions,
        );
      }

      await refreshActivities();
    },
    [activities, createRegions, orgId, refreshActivities],
  );

  const handleCreateCustomPurpose = useCallback(
    async (name: string) => {
      if (existingPurposeNames.includes(name)) {
        return;
      }

      await Api.processingActivities.createProcessingActivity(orgId, {
        name,
        defaultId: undefined,
        lawfulBases: undefined,
        regions: createRegions,
      });
      await refreshActivities();
    },
    [createRegions, existingPurposeNames, orgId, refreshActivities],
  );

  const handleEditCustomPurpose = useCallback(
    async (activity: ProcessingActivityDto, name: string) => {
      const otherNames = existingPurposeNames.filter((existing) => existing !== activity.name);
      if (otherNames.includes(name)) {
        return;
      }

      await Api.processingActivities.patchUpdateProcessingActivity(orgId, activity.id, {
        name,
        lawfulBases: undefined,
        regions: undefined,
      });
      await refreshActivities();
    },
    [existingPurposeNames, orgId, refreshActivities],
  );

  const handleRemoveCustomPurpose = useCallback(
    async (activity: ProcessingActivityDto) => {
      const regions: ProcessingRegion[] = ["UNITED_STATES"];
      if (activity.regions.includes("EEA_UK")) {
        regions.push("EEA_UK");
      }
      if (activity.regions.includes("CANADA")) {
        regions.push("CANADA");
      }

      await Api.processingActivities.deleteProcessingActivityRegion(orgId, activity.id, regions);
      await refreshActivities();
    },
    [orgId, refreshActivities],
  );

  if (!org || !consumerGroups || !activities || !defCheckboxes || !dataMapRequest.success) {
    return <PageLoading />;
  }

  const nameModalExistingNames =
    nameModalState?.mode === "edit"
      ? existingPurposeNames.filter((name) => name !== nameModalState.activity.name)
      : existingPurposeNames;

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        showOnboardingFooter && (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent="Done with Data Purposes"
                nextOnClick={() => {
                  invalidateOnboardingProgress();
                  history.push(Routes.dataMap.dataFlows.sources);
                }}
                nextDisabled={!isPurposesPageComplete(defCheckboxes, activities)}
              />
            }
          />
        )
      }
    >
      <div className="tw-boundary">
        <DataFlowsStickyHeader
          breadcrumb={{
            title: "Data Flows",
            source: "Data Map",
            sourceRoute: Routes.dataMap.dashboard,
          }}
          tabs={[
            {
              name: "Collection Groups",
              tabKey: "collection-groups",
              route: Routes.dataMap.dataFlows.dashboard,
              current: false,
            },
            {
              name: "Purposes",
              tabKey: "purposes",
              route: Routes.dataMap.dataFlows.purposes,
              current: true,
              showIncompleteIndicator: !isPurposesPageComplete(defCheckboxes, activities),
            },
            {
              name: "Sources",
              tabKey: "sources",
              route: Routes.dataMap.dataFlows.sources,
              current: false,
              showIncompleteIndicator:
                sourcesCompletion.isReady && !sourcesCompletion.isSourcesComplete,
              disabled:
                org.finishedOnboardingLatch !== true &&
                stepsCompletion.isReady &&
                !(stepsCompletion.allComplete && stepsCompletion.purposesComplete),
            },
          ]}
        />
        <div className="max-w-[933px] pt-[24px]">
          <PurposesExplanatoryCard showGdprLawfulBases={org.featureGdpr} />
        </div>
        {recommendationDismissal.loaded &&
          !recommendationDismissal.dismissed &&
          !isPurposesPageComplete(defCheckboxes, activities) &&
          recommendedDefaults.length > 0 && (
            <div className="mt-[24px] max-w-[933px]">
              <AlertCard variant="recommendation">
                <div>
                  Most businesses use these processing purposes. We've marked the ones we recommend
                  for your business — hover “Why we recommend” on a purpose to see the reason.
                  <div className="mt-[12px] flex items-center gap-[16px]">
                    <Button
                      color="primary"
                      size="s"
                      disabled={isSelectingRecommended}
                      onClick={async () => {
                        setIsSelectingRecommended(true);
                        try {
                          await defCheckboxes.selectDefaults(recommendedDefaults.map((d) => d.id));
                        } catch (e) {
                          // Leave the banner up so the user can retry; the optimistic checkmarks
                          // revert on the next activities refetch.
                          showSnackbar("Couldn't select those purposes. Please try again.", {
                            variant: "error",
                          });
                          return;
                        } finally {
                          setIsSelectingRecommended(false);
                        }
                        recommendationDismissal.dismiss();
                      }}
                    >
                      Select {recommendedDefaults.length} recommended{" "}
                      {recommendedDefaults.length === 1 ? "purpose" : "purposes"}
                    </Button>
                    <Button color="link" size="s" onClick={recommendationDismissal.dismiss}>
                      Dismiss
                    </Button>
                  </div>
                </div>
              </AlertCard>
            </div>
          )}
        <div className="mt-[24px] space-y-[24px]">
          {defCheckboxes.props.defaults?.map((defActivity) => {
            return (
              <div
                key={defActivity.id}
                id={`purpose-${defActivity.id}`}
                // Match the card's own width cap, or the deep-link ring runs past it (POL-7818).
                className="max-w-[933px]"
                style={focusedPurposeId === defActivity.id ? DEEP_LINK_FOCUS_STYLE : undefined}
              >
                <CollectionPurposeCollapseCard
                  defaultActivity={defActivity}
                  refreshActivities={refreshActivities}
                  activities={activities}
                  checked={defCheckboxes.props.checked}
                  setChecked={defCheckboxes.props.setChecked}
                  categories={collectedPic}
                  assocationMutation={assocationMutation}
                  variant="checkbox"
                  useAquiloDesign={true}
                  showGdprLawfulBases={org.featureGdpr}
                  recommendation={recommendationForDefault(defActivity.name)}
                />
              </div>
            );
          })}
          {customActivities.map((activity) => (
            // Same deep-link target as the default cards: the data-map diagram links custom
            // purposes here too (POL-7818 #7).
            <div
              key={activity.id}
              id={`purpose-${activity.id}`}
              className="max-w-[933px]"
              style={focusedPurposeId === activity.id ? DEEP_LINK_FOCUS_STYLE : undefined}
            >
              <CollectionPurposeCollapseCard
                activity={activity}
                refreshActivities={refreshActivities}
                activities={activities}
                checked={customCheckedIds}
                setChecked={handleCustomToggle}
                categories={collectedPic}
                assocationMutation={assocationMutation}
                variant="checkbox"
                useAquiloDesign={true}
                showGdprLawfulBases={org.featureGdpr}
                showCustomActions={true}
                onEdit={() => setNameModalState({ mode: "edit", activity })}
                onRemove={() => setRemoveModalState({ activity })}
              />
            </div>
          ))}
        </div>
        <div className="mt-[24px] max-w-[933px]">
          <Button color="secondary" onClick={() => setNameModalState({ mode: "add" })}>
            Add a New Purpose
          </Button>
        </div>
        {notProcessedPic.length > 0 && (
          <div className="mt-[24px] max-w-[933px]">
            <AlertCard
              variant="warning"
              title="The following personal information categories are not selected for any collection purposes:"
            >
              <ul className="mt-[8px] list-disc space-y-[4px] pl-[20px]">
                {notProcessedPic.map((pic) => (
                  <li key={pic.id}>{pic.name}</li>
                ))}
              </ul>
            </AlertCard>
          </div>
        )}
      </div>

      <CustomPurposeNameModal
        open={nameModalState?.mode === "add"}
        title="Add a New Purpose"
        submitLabel="Add"
        existingNames={nameModalExistingNames}
        onClose={() => setNameModalState(null)}
        onSubmit={handleCreateCustomPurpose}
      />

      <CustomPurposeNameModal
        open={nameModalState?.mode === "edit"}
        title="Edit Purpose"
        submitLabel="Save"
        initialName={nameModalState?.mode === "edit" ? nameModalState.activity.name : ""}
        existingNames={nameModalExistingNames}
        onClose={() => setNameModalState(null)}
        onSubmit={async (name) => {
          if (nameModalState?.mode === "edit") {
            await handleEditCustomPurpose(nameModalState.activity, name);
          }
        }}
      />

      <RemoveCustomPurposeModal
        open={removeModalState != null}
        purposeName={removeModalState?.activity.name ?? ""}
        onClose={() => setRemoveModalState(null)}
        onConfirm={async () => {
          if (removeModalState) {
            await handleRemoveCustomPurpose(removeModalState.activity);
            setRemoveModalState(null);
          }
        }}
      />
    </StandardPageWrapper>
  );
}
